import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import type { ProposalTemplate } from "@/types/proposals";
import { nanoid } from "nanoid";
import { EstimateLineItemSelector } from "./EstimateLineItemSelector";
import { PROPOSAL_TEMPLATES } from "@/data/proposal-templates";

interface CreateProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobData?: any;
  estimateId?: string | null;
}

interface LineItem {
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total: number;
  is_included?: boolean;
}

export function CreateProposalDialog({ open, onOpenChange, jobId, jobData, estimateId }: CreateProposalDialogProps) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [estimateLineItems, setEstimateLineItems] = useState<LineItem[]>([]);
  const [showLineItemSelector, setShowLineItemSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ProposalTemplate | null>(null);
  const [selectedItems, setSelectedItems] = useState<LineItem[]>([]);
  const [showAsLumpSum, setShowAsLumpSum] = useState(false);

  useEffect(() => {
    if (open) {
      loadTemplates();
      if (estimateId) {
        loadEstimateLineItems();
      }
    }
  }, [open, estimateId]);

  const loadTemplates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) return;

      // Load custom templates from database
      const { data: customTemplates, error } = await supabase
        .from("proposal_templates")
        .select("*")
        .eq("org_id", profile.org_id)
        .eq("doc_type", "proposal")
        .order("is_default", { ascending: false });

      if (error) throw error;

      // Convert pre-built templates to a consistent format
      const preBuiltTemplates = PROPOSAL_TEMPLATES.map(t => ({
        id: t.id, // Use original ID, no prefix
        name: t.name,
        description: t.description,
        is_default: false,
        is_system_template: true,
        layout_json: t.layout,
        layout: t.layout, // Add both for compatibility
        org_id: profile.org_id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        doc_type: 'proposal' as const,
      }));
      
      // Show only pre-built templates (no duplicates, no broken custom templates)
      setTemplates(preBuiltTemplates as unknown as ProposalTemplate[]);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error("Failed to load templates");
    }
  };

  const loadEstimateLineItems = async () => {
    if (!estimateId) return;

    try {
      const { data, error } = await supabase
        .from("estimate_line_items")
        .select("*")
        .eq("estimate_id", estimateId)
        .order("line_order", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        const items: LineItem[] = data.map(item => ({
          description: item.item_name || item.description || 'Item',
          quantity: item.quantity || 1,
          unit: item.unit_type || 'sq',
          unit_price: item.unit_price || 0,
          total: item.total_cost || 0,
          is_included: true,
        }));
        setEstimateLineItems(items);
      }
    } catch (error) {
      console.error("Error loading estimate line items:", error);
      toast.error("Failed to load estimate line items");
    }
  };

  const handleSelectTemplate = (template: ProposalTemplate) => {
    setSelectedTemplate(template);
    if (estimateLineItems.length > 0) {
      setShowLineItemSelector(true);
    } else {
      createProposal(template, [], false);
    }
  };

  const handleLineItemsChange = (items: LineItem[], lumpSum: boolean) => {
    setSelectedItems(items);
    setShowAsLumpSum(lumpSum);
  };

  const handleConfirmLineItems = () => {
    if (!selectedTemplate) {
      toast.error("Please select a template first");
      return;
    }
    createProposal(selectedTemplate, selectedItems, showAsLumpSum);
  };

  const createProposal = async (template: ProposalTemplate, items: LineItem[], lumpSum: boolean) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .maybeSingle();

      if (profileError) {
        console.error("Profile fetch error:", profileError);
        throw new Error("Failed to fetch user profile");
      }

      if (!profile?.org_id) {
        throw new Error("Organization not found");
      }

      // Merge job data into template
      const filledLayoutSource = (template as any).layout_json || (template as any).layout;
      const filledLayout = JSON.parse(JSON.stringify(filledLayoutSource));
      
      // Get organization data for replacements
      const { data: org } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", profile.org_id)
        .single();

      // Replace variables with job data
      const replaceVariables = (obj: any): any => {
        if (typeof obj === "string") {
          return obj
            .replace(/\{\{customer_name\}\}/g, jobData?.owner_name || "Customer Name")
            .replace(/\{\{property_address\}\}/g, jobData?.property_address || "Property Address")
            .replace(/\{\{current_date\}\}/g, new Date().toLocaleDateString())
            .replace(/\{\{org_name\}\}/g, org?.name || "Your Company")
            .replace(/\{\{org_phone\}\}/g, org?.phone || "")
            .replace(/\{\{org_email\}\}/g, org?.email || "")
            .replace(/\{\{trade_name\}\}/g, jobData?.trade_name || "Service")
            .replace(/\{\{trade_type\}\}/g, jobData?.trade_type || "");
        }
        if (Array.isArray(obj)) {
          return obj.map(replaceVariables);
        }
        if (typeof obj === "object" && obj !== null) {
          const newObj: any = {};
          for (const key in obj) {
            newObj[key] = replaceVariables(obj[key]);
          }
          return newObj;
        }
        return obj;
      };

      let layoutWithData: any = replaceVariables(filledLayout);

      // Process line items - filter included items
      const includedItems = items.filter(item => item.is_included);
      
      // Calculate totals
      const subtotal = includedItems.reduce((sum, item) => sum + item.total, 0);
      const tax = subtotal * 0.08;
      const total = subtotal + tax;

      // Prepare line items for proposal
      let proposalLineItems: any[] = [];
      
      if (lumpSum) {
        // Show as single lump sum entry with dynamic service name
        const serviceName = jobData?.trade_name || jobData?.trade_type || "Complete Project";
        proposalLineItems = [{
          description: serviceName,
          quantity: 1,
          unit: "project",
          unit_price: subtotal,
          total: subtotal,
        }];
      } else {
        // Show individual line items
        proposalLineItems = includedItems;
      }

      // For system templates, use the original ID without prefix
      const templateId = template.is_system_template ? null : template.id;

      // Inject line items into all line_items_table blocks in the layout
      layoutWithData = {
        ...layoutWithData,
        pages: layoutWithData.pages.map((page: any) => ({
          ...page,
          blocks: page.blocks.map((block: any) => {
            if (block.type === 'line_items_table') {
              return {
                ...block,
                content: {
                  ...block.content,
                  useDynamicData: false, // Use static items instead of dynamic
                  items: proposalLineItems.map(item => ({
                    name: item.description,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    total: item.total
                  }))
                }
              };
            }
            return block;
          })
        }))
      };

      // Create title with trade info if available
      const proposalTitle = jobData?.trade_name 
        ? `${jobData?.owner_name || "Customer"} - ${jobData.trade_name} Proposal`
        : `${jobData?.owner_name || "Customer"} - Proposal`;

      const { data: proposalDoc, error } = await supabase
        .from("proposal_documents")
        .insert([{
          job_id: jobId,
          org_id: profile.org_id,
          template_id: templateId,
          doc_type: "proposal",
          status: "draft",
          title: proposalTitle,
          layout_filled_json: layoutWithData as any,
          public_share_id: nanoid(12),
          created_by: user.id,
          estimate_id: estimateId || null,
        }])
        .select()
        .maybeSingle();

      if (error) {
        console.error("Proposal insert error:", error);
        throw new Error(`Failed to insert proposal: ${error.message}`);
      }
      
      if (!proposalDoc) {
        throw new Error("Proposal document was not created");
      }

      await supabase.from("proposal_activities").insert({
        proposal_id: proposalDoc.id,
        activity_type: "created",
      });

      toast.success(`Proposal created with ${includedItems.length} line items${lumpSum ? ' (shown as lump sum)' : ''}`);
      onOpenChange(false);
      setShowLineItemSelector(false);
      navigate(`/proposals/review/${proposalDoc.id}`);
    } catch (error: any) {
      console.error("Error creating proposal:", error);
      toast.error(error?.message || "Failed to create proposal");
    } finally {
      setLoading(false);
    }
  };

  if (showLineItemSelector) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Line Items for Proposal</DialogTitle>
            <DialogDescription>
              Select which line items to include and choose display options
            </DialogDescription>
          </DialogHeader>

          <EstimateLineItemSelector
            items={estimateLineItems}
            onItemsChange={handleLineItemsChange}
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowLineItemSelector(false)}
            >
              Back to Templates
            </Button>
            <Button
              onClick={handleConfirmLineItems}
              disabled={loading || selectedItems.filter(i => i.is_included).length === 0}
            >
              Create Proposal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Proposal</DialogTitle>
          <DialogDescription>
            Choose a template to create a new proposal for this job
            {estimateLineItems.length > 0 && ` (${estimateLineItems.length} line items ready to import)`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.length === 0 ? (
            <div className="col-span-2 text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No templates found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/templates")}
              >
                Create a template first
              </Button>
            </div>
          ) : (
            templates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-primary/50 transition-all"
                onClick={() => handleSelectTemplate(template)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{template.name}</span>
                    {template.is_default && (
                      <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                        Default
                      </span>
                    )}
                  </CardTitle>
                  {template.description && (
                    <CardDescription>{template.description}</CardDescription>
                  )}
                </CardHeader>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
